import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Ticket, Power } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, useAdmin } from "@/components/admin/ui";

const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#1F2A22]";
const money = (c) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100);
const dtLocal = (v) => (v ? String(v).slice(0, 16) : "");

const BLANK = { code: "", type: "percent", value: 10, min_subtotal_dollars: 0, max_uses: "", starts_at: "", ends_at: "" };

function describe(d) {
  if (d.type === "percent") return `${d.value}% off`;
  if (d.type === "fixed") return `${money(d.value)} off`;
  return "Free shipping";
}

export default function AdminCommerceDiscounts() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("marketplace");
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.admDiscounts().then(setRows).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.code.trim()) { toast.error("Enter a code"); return; }
    setBusy(true);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: form.type === "fixed" ? Math.round(Number(form.value) * 100) : Number(form.value),
        min_subtotal: Math.round(Number(form.min_subtotal_dollars || 0) * 100),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      };
      await api.admDiscountCreate(body);
      toast.success("Discount created");
      setForm(BLANK);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create"); }
    finally { setBusy(false); }
  };

  const toggle = async (d) => { try { await api.admDiscountToggle(d.id); load(); } catch { toast.error("Failed"); } };
  const del = async (d) => { if (!window.confirm(`Delete code "${d.code}"?`)) return; try { await api.admDiscountDelete(d.id); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-commerce-discounts">
      <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Discount codes</h1></div>
      <p className="text-sm text-[#5C6B61] mt-2">Create promo codes shoppers apply in the HomeRange cart. Percent and fixed codes discount the product subtotal; free-shipping codes waive the shipping charge.</p>

      {canEdit && (
        <div className="bg-white border border-[#E5E2DC] p-5 mt-5" data-testid="discount-form">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Code</span>
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inp + " mt-1"} placeholder="FALL20" data-testid="discount-code-input" /></label>
            <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Type</span>
              <select value={form.type} onChange={set("type")} className={inp + " mt-1"} data-testid="discount-type-select">
                <option value="percent">Percent off</option><option value="fixed">Fixed amount off</option><option value="free_shipping">Free shipping</option>
              </select></label>
            {form.type !== "free_shipping" && (
              <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">{form.type === "percent" ? "Percent (%)" : "Amount ($)"}</span>
                <input type="number" min="0" value={form.value} onChange={set("value")} className={inp + " mt-1"} data-testid="discount-value-input" /></label>
            )}
            <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Min. subtotal ($)</span>
              <input type="number" min="0" value={form.min_subtotal_dollars} onChange={set("min_subtotal_dollars")} className={inp + " mt-1"} data-testid="discount-min-input" /></label>
            <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Max uses (blank = ∞)</span>
              <input type="number" min="0" value={form.max_uses} onChange={set("max_uses")} className={inp + " mt-1"} data-testid="discount-maxuses-input" /></label>
            <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Starts (optional)</span>
              <input type="datetime-local" value={dtLocal(form.starts_at)} onChange={set("starts_at")} className={inp + " mt-1"} data-testid="discount-starts-input" /></label>
            <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Ends (optional)</span>
              <input type="datetime-local" value={dtLocal(form.ends_at)} onChange={set("ends_at")} className={inp + " mt-1"} data-testid="discount-ends-input" /></label>
          </div>
          <button onClick={create} disabled={busy} className={btnPrimary + " mt-4"} data-testid="discount-create-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create code</button>
        </div>
      )}

      <div className="mt-6">
        {rows === null ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-[#5C6B61]"><Ticket size={34} className="mx-auto text-[#C7C2B8]" /><p className="mt-3">No discount codes yet.</p></div>
        ) : (
          <div className="border border-[#E5E2DC] bg-white overflow-x-auto" data-testid="discount-list">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[#8A8478] border-b border-[#E5E2DC]">
                <th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Discount</th><th className="px-4 py-2.5">Min</th><th className="px-4 py-2.5">Uses</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="border-b border-[#F0EEE9] last:border-0" data-testid={`discount-row-${d.code}`}>
                    <td className="px-4 py-2.5 font-mono font-semibold text-[#1F2A22]">{d.code}</td>
                    <td className="px-4 py-2.5 text-[#1F2A22]">{describe(d)}</td>
                    <td className="px-4 py-2.5 text-[#8A8478]">{d.min_subtotal ? money(d.min_subtotal) : "—"}</td>
                    <td className="px-4 py-2.5 text-[#8A8478]">{d.used_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ""}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${d.active ? "bg-[#2E7D46]/15 text-[#2E7D46]" : "bg-[#E5E2DC] text-[#5C6B61]"}`}>{d.active ? "Active" : "Off"}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button disabled={!canEdit} onClick={() => toggle(d)} className="text-xs border border-[#E5E2DC] text-[#5C6B61] px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40" data-testid={`discount-toggle-${d.code}`}><Power size={12} /> {d.active ? "Disable" : "Enable"}</button>
                        <button disabled={!canEdit} onClick={() => del(d)} className="text-xs border border-[#B04A3A] text-[#B04A3A] px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40" data-testid={`discount-delete-${d.code}`}><Trash2 size={12} /> Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
